import { Button } from '@akin-home/buttons/native';
import { convertToNon24HourFormat, isValidTimeFormat } from '@akin-home/dates';
import { Text } from '@akin-home/text/native';
import { colors } from '@akin-home/theme';
import { useState } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';
import useBool from '../../utils/state/useBool';
import Dialog from '../Dialog';
import { DialogProps } from '../Dialog/Dialog';
import { SelectHourDialog } from '../SelectHourDialog';
import { SelectMinuteDialog } from '../SelectMinuteDialog';

interface Props extends DialogProps {
  initial_value?: string;
  title?: string;
  onSubmit(time: string): void;
}

export const EditTimeDialog = ({
  initial_value,
  title,
  onSubmit,
  ...rest
}: Props) => {
  const [value, setValue] = useState(
    isValidTimeFormat(initial_value) ? initial_value : '07:00'
  );

  const [hour_picker_visible, showHourPicker, hideHourPicker] = useBool();
  const [minute_picker_visible, showMinutePicker, hideMinutePicker] = useBool();

  const [hour_value, minute_value] = value.split(':');
  const is_AM = hour_value < '12';
  const hour_display_value = convertToNon24HourFormat(hour_value);

  const onPressAM = () => {
    if (!is_AM) {
      const hour = parseInt(hour_value) - 12;
      setValue(hour.toString().padStart(2, '0') + ':' + minute_value);
    }
  };

  const onPressPM = () => {
    if (is_AM) {
      const hour = parseInt(hour_value) + 12;
      setValue(hour.toString() + ':' + minute_value);
    }
  };

  const submit = () => onSubmit(value);

  const onChangeHour = (hour: string) => {
    setValue(hour + `:${minute_value}`);
    hideHourPicker();
  };

  const onChangeMinute = (minute: string) => {
    setValue(`${hour_value}:${minute}`);
    hideMinutePicker();
  };

  return (
 
      <Dialog size="sm" {...rest}>
        <Body>
          <Text variant="h3" weight="semibold">
            {title || 'Select a time'}
          </Text>

          <MainRow>
            <TimeInputs>
              <HourInput onPress={showHourPicker}>
                <TimeText weight="semibold">{hour_display_value}</TimeText>
              </HourInput>
              <Colon>:</Colon>
              <MinuteInput onPress={showMinutePicker}>
                <TimeText weight="semibold" color={colors.neutral[60]}>
                  {minute_value}
                </TimeText>
              </MinuteInput>
            </TimeInputs>

            <View>
              <AMPM>
                <AMPMButton active={is_AM} onPress={onPressAM}>
                  <Text weight="bold" color={is_AM ? 'white' : undefined}>
                    AM
                  </Text>
                </AMPMButton>
                <AMPMButton active={!is_AM} onPress={onPressPM}>
                  <Text weight="bold" color={!is_AM ? 'white' : undefined}>
                    PM
                  </Text>
                </AMPMButton>
              </AMPM>
            </View>
          </MainRow>
        </Body>

        <Actions>
          <Button variant="text" onPress={rest.onClose}>
            Cancel
          </Button>

          <Button onPress={submit}>Continue</Button>
        </Actions>

        <SelectHourDialog
          visible={hour_picker_visible}
          value={hour_value}
          onSelect={onChangeHour}
          onClose={hideHourPicker}
        />

        <SelectMinuteDialog
          visible={minute_picker_visible}
          value={minute_value}
          onSelect={onChangeMinute}
          onClose={hideMinutePicker}
        />
      </Dialog>
  );
};

const Body = styled.View`
  align-items: center;
  padding: 30px;
  gap: 30px;
`;

const MainRow = styled.View`
  flex-direction: row;
  gap: 12px;
`;

const TimeInputs = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 5px;
`;

const AMPM = styled.View`
  border-radius: 8px;
  border-color: ${colors.neutral[60]}
  border-width: 1px;
  height: 40px;
  width: 52px;
  flex: 1;
  overflow: hidden;
`;

const AMPMButton = styled.TouchableOpacity<{ active?: boolean }>`
  align-items: center;
  justify-content: center;
  flex: 1;
  ${({ active }) => (active ? `background-color: ${colors.primary[700]};` : '')}
`;

const HourInput = styled.TouchableOpacity`
  height: 96px;

  padding: 0 10px;

  border-color: #535af1;
  border-width: 2px;
  border-radius: 22px;

  align-items: center;
  justify-content: center;
`;

const MinuteInput = styled.TouchableOpacity`
  height: 96px;

  padding: 0 10px;

  background-color: #f4f4f4;
  border-radius: 8px;

  align-items: center;
  justify-content: center;
`;

const Colon = styled(Text)`
  font-size: 57px;
  line-height: 64px;
`;

const TimeText = styled(Text)`
  font-size: 57px;
  line-height: 68px;
`;

const Actions = styled.View`
  flex-direction: row;
  justify-content: space-between;

  border-top-width: 1px;
  border-top-color: #d0d0d0;
  padding: 15px 32px;
`;
